import {
    currentOnlineBriefingOptionVersion,
    OnlineBriefingTemplateConfigModel,
    OnlineBriefingOptionVersion,
} from '../models/online-briefing-option.model';

type TransformationFunction = (oldDataModel: OnlineBriefingTemplateConfigModel) => OnlineBriefingTemplateConfigModel;

/**
 * Handles transformations for versioning OnlineBriefingTemplateConfigModel JSON objects.
 *
 * Any breaking / non-backwards compatible changes (deletions, breaking updates) should be accompanied by a version change.
 * Adding fields or options to a field are non-breaking and do not require a version change.
 * Version changes should be encapsulated by a transformation function, which is then mapped to the previous version.
 * e.g.,
 *      Current Version {@link currentOnlineBriefingOptionVersion}
 *      Some breaking change is added
 *      '1.1' added to {@link OnlineBriefingOptionVersion} union
 *      {@link currentOnlineBriefingOptionVersion} updated to '1.1'
 *      Transformation function 'fromOnePointOhToOnePointOne' is created
 *      A new entry in the {@link transformationJumpTable} is added:
 *          - ['1.0']: fromOnePointOhToOnePointOne,
 */
export class OnlineBriefingOptionVersioner {
    private readonly transformationJumpTable: { [k in OnlineBriefingOptionVersion]?: TransformationFunction } = {};

    transformDataToNewestVersion(oldDataModel: OnlineBriefingTemplateConfigModel): OnlineBriefingTemplateConfigModel {
        let currentData: OnlineBriefingTemplateConfigModel = oldDataModel;
        while (this.versionAsNumber(currentData) < this.versionAsNumber(currentOnlineBriefingOptionVersion)) {
            currentData = this.transformationJumpTable[this.getVersion(currentData)](currentData);
        }
        return currentData;
    }

    private versionAsNumber(modelOrVersion: OnlineBriefingTemplateConfigModel | OnlineBriefingOptionVersion): number {
        if (typeof modelOrVersion === 'object') {
            const version = this.getVersion(modelOrVersion);
            return Number(version);
        }
        return Number(modelOrVersion);
    }

    private getVersion(model: OnlineBriefingTemplateConfigModel): OnlineBriefingOptionVersion {
        return model?.version ?? '1.0';
    }
}
